import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from '@mui/material/Chip';
import Dialog from "@mui/material/Dialog";
import Drawer from '@mui/material/Drawer';
import Button from "@mui/material/Button";
import SvgIcon from "@mui/material/SvgIcon";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DialogTitle from "@mui/material/DialogTitle";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import GlobalListOptions from "./GlobalListOptions";
import "./FroalaEditorStyles.scss";
import "../../../assets/styles/containers/_commentLibraryModal.scss";
import "../../../assets/styles/containers/_globalList.scss";
import { arrowDown } from "../../../assets/svgIcons/Svg";
import { getGlobalList } from "../../store/action";
import { CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import Slide from '@mui/material/Slide';

const IOSSwitch = styled((props) => (
    <Switch {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 2,
        transitionDuration: "300ms",
        "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                backgroundColor: "#239D80",
                opacity: 1,
                border: 0,
                ...theme.applyStyles("dark", { backgroundColor: "#239D80" }),
            },
            "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": { color: "#33cf4d", border: "6px solid #fff" },
        "&.Mui-disabled .MuiSwitch-thumb": {
            color: theme.palette.grey[100],
            ...theme.applyStyles("dark", { color: theme.palette.grey[600] }),
        },
        "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.7, ...theme.applyStyles("dark", { opacity: 0.3 }) },
    },
    "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 22, height: 22 },
    "& .MuiSwitch-track": {
        borderRadius: 26 / 2,
        backgroundColor: "#E9E9EA",
        opacity: 1,
        transition: theme.transitions.create(["background-color"], { duration: 500 }),
        ...theme.applyStyles("dark", { backgroundColor: "#39393D" }),
    },
}));

const SlideFromRight = React.forwardRef(function SlideFromRight(
    props,
    ref,
) {
    return <Slide direction="left" ref={ref} {...props} />;
});
function EditOptionsDialog(props) {
    const { state, setState, handleSave, id, dialogRef, handleDeleteDropdown, filterTagList, addDropdown, names, canSave, categoryTags, newOpen } = props;
    const { open, newOption, dialogOptionList, globalLists, saveToGlobalLists, name, tags, description, listId, isGlobalList, selectedDropdown, isDialogLoading } = state;
    const [selectedFilterTagID, setSelectedFilterTagID] = useState(null);
    const [isEdit, setIsEdit] = useState(-1);
    const [tagOpen, setTagOpen] = useState(false);
    const [searchData, setSearchData] = useState(null);
    const [tagInputValue, setTagInputValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const dispatch = useDispatch();
    const globalList = useSelector((state) => state.globalList);
    const pageRef = useRef(1);
    const hasMoreRef = useRef(true);
    const isLoadingRef = useRef(false);
    const resetFlagRef = useRef(false);
    const globalListOptionsRef = useRef(null);
    const editInputRef = useRef(null);
    const location = useLocation();
    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    useEffect(() => {
        if (open && isEdit !== -1 && editInputRef.current) {
            editInputRef.current.focus();
        }
    }, [isEdit, dialogOptionList?.length, open]);

    const handleTagOpen = (e) => {
        setTagOpen(true);
    };

    const handleTagClose = (e) => {
        setTagOpen(false);
    };

    const handleCloseDialog = () => {
        setState((prevState) => ({ ...prevState, open: false, dialogOptionList: [], textfield: false, editedOption: null, globalLists: null, name: "", tags: [], description: "", saveToGlobalLists: false, listId: null, listGuid: null, searchData: null, selectedFilterTagID: null }));
        setSearchData(null);
        setSelectedFilterTagID(null)
    };

    const handleOptionBlur = () => {
        if (isEdit !== -1) {
            setState((prevState) => {
                let updatedOptions = [...prevState.dialogOptionList];
                if (updatedOptions[isEdit] !== undefined) {
                    updatedOptions[isEdit] = updatedOptions[isEdit].replace(/^\s+|\s+$/g, '');
                }
                return { ...prevState, dialogOptionList: updatedOptions, newOption: "" };
            });
            setIsEdit(-1);
        }
    };

    const handleOptionClick = (option, index) => {
        if (isEdit !== index) {
            setIsEdit(index);
            setState((prevState) => ({ ...prevState, newOption: option }));
        }
    };

    const handleOptionChange = (e, index) => {
        const val = e.target.value;
        if (isEdit === index) {
            setState((prevState) => {
                const updatedOptions = [...prevState.dialogOptionList];
                updatedOptions[index] = val;
                return { ...prevState, newOption: val, dialogOptionList: updatedOptions };
            });
        }
    };

    const handleDelete = (event, index) => {
        event.stopPropagation();
        handleOptionBlur();
        setState((prevState) => {
            let updatedOptions = [...prevState.dialogOptionList];
            updatedOptions.splice(index, 1);
            return { ...prevState, dialogOptionList: updatedOptions, newOption: "", isEdit: -1 };
        });
    };

    const onDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;
        const updatedList = Array.from(dialogOptionList);
        const [movedItem] = updatedList.splice(source.index, 1);
        updatedList.splice(destination.index, 0, movedItem);
        setState((prevState) => ({ ...prevState, dialogOptionList: updatedList }));
        if (isEdit !== -1) {
            setIsEdit(-1);
            setState((prevState) => ({ ...prevState, newOption: "" }));
        }
    };

    const onDragStart = () => {
        if (isEdit !== -1) handleOptionBlur();
    };

    const getGlobalLists = useCallback((pageNum = 0, searchText, tags) => {
        if (isLoadingRef.current) return;
        setIsLoading(true);
        isLoadingRef.current = true;
        pageRef.current = pageNum;
        dispatch(getGlobalList(pageNum > 0 ? pageNum - 1 : 0, 30, searchText, location.pathname.includes("templates") ? tags : []));
    }, [dispatch]);

    useEffect(() => {
        if (globalList?.globalListData) {
            const pagination = globalList?.globalListPagination;
            const hasMorePages = pagination?.current_page < pagination?.total_pages;
            setHasMore(hasMorePages);
            hasMoreRef.current = hasMorePages;
            setCurrentPage(pagination?.current_page || 1);
            setIsLoading(false);
            isLoadingRef.current = false;
            if (globalListOptionsRef.current) {
                globalListOptionsRef.current.resetLoadingRef();
            }
            setState((prevState) => {
                if (pageRef.current === 1 || resetFlagRef.current) {
                    resetFlagRef.current = false;
                    return { ...prevState, globalLists: globalList.globalListData };
                } else {
                    const existingIds = new Set(prevState.globalLists?.map(item => item?.id));
                    const newItems = globalList?.globalListData?.filter(item => !existingIds.has(item?.id));
                    return {
                        ...prevState,
                        globalLists: [
                            ...(Array.isArray(prevState.globalLists) ? prevState.globalLists : []),
                            ...(Array.isArray(newItems) ? newItems : []),
                        ],
                    };
                }
            });
        }
    }, [globalList?.globalListData, globalList?.globalListPagination]);

    useEffect(() => {
        if (!open) {
            setTagInputValue("");
        }
    }, [open]);

    useEffect(() => {
        if (open === "global_lists") {
            if (selectedFilterTagID === null) {
                const catTagIds = (categoryTags || []).map(tag => tag.id);
                setSelectedFilterTagID(catTagIds);
            } else {
                resetFlagRef.current = true;
                pageRef.current = 1;
                setCurrentPage(1);
                setHasMore(true);
                hasMoreRef.current = true;
                setState((prevState) => ({ ...prevState, globalLists: [] }));
                getGlobalLists(0, searchData, location.pathname.includes("templates") ? selectedFilterTagID : []);
            }
        }
    }, [searchData, selectedFilterTagID, open, getGlobalLists, categoryTags]);

    const renderEditOptionsContent = (component) => {
        return isDialogLoading ? <Grid display={"flex"} height={"100%"} justifyContent={"center"} alignItems={"center"}><CircularProgress />
        </Grid> : (open === "global_lists" ?
            <GlobalListOptions
                ref={globalListOptionsRef}
                setState={setState}
                globalLists={globalLists}
                filterTagList={filterTagList}
                getGlobalLists={getGlobalLists}
                selectedFilterTagID={selectedFilterTagID}
                setSelectedFilterTagID={setSelectedFilterTagID}
                searchData={searchData}
                setSearchData={setSearchData}
                addDropdown={addDropdown}
                isLoading={isLoading}
                hasMore={hasMore}
                currentPage={currentPage}
            /> : <Grid container>
                {!isGlobalList && !listId && (
                    <Grid item xs={12} my={1} px={component === "drawer" ? 2 : 0}>
                        <FormGroup>
                            <Box display="flex" alignItems="center" gap={1}>
                                <IOSSwitch
                                    sx={{ m: 1, ml: 2, cursor: "pointer" }}
                                    disabled={open === "edit_list" && listId !== undefined}
                                    checked={saveToGlobalLists}
                                    onChange={(e) =>
                                        setState({
                                            ...state,
                                            saveToGlobalLists: e.target.checked,
                                            tags: location.pathname.includes("templates") ? e.target.checked && (!state.tags || state.tags.length === 0) ? categoryTags : state.tags : []
                                        })
                                    }
                                />
                                <Typography className="save_to_global_lists" sx={{ cursor: "default", userSelect: "none" }}>
                                    Save to global lists
                                </Typography>
                            </Box>
                        </FormGroup>
                    </Grid>
                )
                }
                {saveToGlobalLists ? <Grid container>
                    <Grid item xs={12} mb={2} px={component === "drawer" ? 2 : 0}>
                        <Typography className="edit_dialog_subtitle">Name</Typography>
                        <TextField value={(/^dropdown\s+[a-z0-9-]+$/i.test(name)) ? "" : name} sx={{ marginLeft: "10px", paddingRight: "20px", }} fullWidth size="small" onChange={(e) => setState(prevState => ({ ...prevState, name: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} mb={2} px={component === "drawer" ? 2 : 0}>
                        <Typography className="edit_dialog_subtitle">Tags</Typography>
                        <Autocomplete
                            className="forms-control-tag autocompleteCustom-list"
                            multiple
                            size="small"
                            limitTags={2}
                            options={filterTagList || []}
                            value={tags || []}
                            inputValue={tagInputValue}
                            onInputChange={(event, newInputValue, reason) => {
                                setTagInputValue(newInputValue)
                            }}
                            open={tagOpen}
                            onOpen={handleTagOpen}
                            onClose={handleTagClose}
                            disableCloseOnSelect
                            clearOnBlur={false}
                            componentsProps={{
                                popper: {
                                    sx: { zIndex: 100000 }
                                }
                            }}
                            getOptionLabel={(option) => option?.name || option?.title || ""}
                            isOptionEqualToValue={(option, value) => option.id === (value?.id || value?.pivot?.tag_id)}
                            filterOptions={(options, { inputValue }) => options?.filter((opt) => (opt?.name || opt?.title)?.toLowerCase()?.includes(inputValue?.toLowerCase()))}
                            onChange={(e, value) => setState((prev) => ({ ...prev, tags: value }))}
                            sx={{
                                marginLeft: "10px",
                                paddingRight: "10px",
                                "& .MuiOutlinedInput-root": {
                                    minHeight: "36px",
                                    alignItems: "center",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                                },
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        key={option.id}
                                        size="small"
                                        style={{
                                            borderRadius: "35px",
                                            border: "1px solid #CED2DA",
                                            backgroundColor: option?.color,
                                            color: (option?.color?.toUpperCase().includes("#FFFFFF") || option?.color?.toLowerCase().includes("white")) ? "#000000" : "#FFFFFF"
                                        }}
                                        deleteIcon={
                                            <div style={{
                                                backgroundColor: "#97A1AF",
                                                borderRadius: "50%",
                                                height: "16px",
                                                width: "16px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                            >
                                                <CloseIcon style={{ color: "white", height: "14px", width: "14px" }} />
                                            </div>
                                        }
                                        label={<span >{option.name || option.title}</span>}
                                        {...getTagProps({ index })}
                                        sx={{
                                            padding: "2px 2px 2px 10px !important",
                                            fontSize: "10px !important",
                                        }}
                                    />
                                ))
                            }
                            renderOption={(props, option, { selected }) => (
                                <li {...props} key={option.id}>
                                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: option.color, marginRight: 1 }} />
                                    {option.name || option.title}
                                </li >
                            )
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400, paddingLeft: tags?.length !== 0 ? "0px" : "20px" },
                                        endAdornment: (
                                            <>
                                                <SearchIcon className={`customSearch ${tags?.length !== 0 ? "hidden" : ""}`} style={{ color: "#9b9b9b", marginRight: "8px" }} />
                                                <SvgIcon className={`customArrowDown ${tagOpen ? "arrowActive" : ""}`} viewBox="" onClick={!tagOpen ? handleTagOpen : handleTagClose}>
                                                    {arrowDown}
                                                </SvgIcon>
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid >
                    <Grid item xs={12} mb={2} px={component === "drawer" ? 2 : 0}>
                        <Typography className="edit_dialog_subtitle">Description</Typography>
                        <TextField value={description} multiline rows={4} sx={{ marginLeft: "10px", paddingRight: "20px", }} fullWidth size="small" onChange={(e) => setState(prevState => ({ ...prevState, description: e.target.value }))} />
                    </Grid>
                </Grid > : null
                }
                <Typography className="edit_dialog_subtitle" sx={{ px: component === "drawer" ? 2 : 0 }} >Options</Typography>
                <Grid item xs={12} ml={1.3} mb={1.5} px={component === "drawer" ? 2 : 0}>
                    <Button
                        startIcon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: "18px" }} />}
                        className="add-option-button"
                        variant="outlined"
                        onClick={() => {
                            setState((prevState) => ({
                                ...prevState,
                                dialogOptionList: ["", ...(prevState.dialogOptionList || [])],
                                newOption: ""
                            }));
                            setIsEdit(0);
                        }}>
                        Add new option
                    </Button>
                </Grid>
                {
                    dialogOptionList?.length === 0 && (
                        <Grid item xs={12} mb={2} px={component === "drawer" ? 2 : 0}>
                            <Typography sx={{ fontSize: "14px", fontWeight: 500, alignItems: "center", justifyContent: "center", textAlign: "center", color: "#777777" }}>
                                At least one option is required to add a list.
                            </Typography>
                        </Grid>
                    )
                }
                <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div style={{ width: "100%" }} ref={provided.innerRef} {...provided.droppableProps}>
                                {dialogOptionList?.map((option, index) => (
                                    <Draggable key={`option_${index}`} draggableId={String(`option_${index}`)} index={index}>
                                        {(provided) => (
                                            <Grid container item px={component === "drawer" ? 2 : 0} xs={12} display={"flex"} flexWrap={'nowrap'} gap={"10px"} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                <Grid item xs={10.5} sm={10.5} md={11} className="option-element">
                                                    <div {...provided.dragHandleProps}>
                                                        <DragIndicatorIcon className="option-drag-icon" />
                                                    </div>
                                                    <TextField
                                                        value={isEdit === index ? newOption : option}
                                                        size="small"
                                                        autoFocus={isEdit === index}
                                                        inputRef={isEdit === index ? editInputRef : null}
                                                        onClick={() => handleOptionClick(option, index)}
                                                        onChange={(e) => handleOptionChange(e, index)}
                                                        onBlur={() => handleOptionBlur(index)}
                                                        className="option-textfield"
                                                        inputProps={{ maxLength: 250 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={1} sm={0.8} md={0.5} mt={-0.3} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                                                    <IconButton onClick={(event) => handleDelete(event, index)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        )
                                        }
                                    </Draggable >
                                ))}
                                {provided.placeholder}
                            </div >
                        )}
                    </Droppable >
                </DragDropContext >
            </Grid >)
    }

    const renderEditOptionsTitle = (component) => {
        return (
            <Grid container display={"flex"} alignItems={"center"} mt={-2} mb={1.5} marginLeft={component === "drawer" ? 0 : 1.4}>
                {component === "drawer" ? <Grid item mt={3.5} alignItems={"center"} xs={component === "drawer" ? 1 : 0.3} display={"flex"} justifyContent={"flex-end"} pr={component === "drawer" ? 2 : 1} ><ArrowBackIcon className="edit_dialog_close" onClick={handleCloseDialog} /></Grid> : null}
                <Grid item display={"flex"} justifyContent={"flex-start"} alignItems={"center"} gap={0} xs={11}>
                    <Typography className="edit-dialog-title">
                        {open === "new_list" && state.isGlobalList ? "Add list" : open === "new_list" ? "New list" : open === "edit_list" ? "Edit list" : "Global lists"}
                    </Typography>
                    {((open === "new_list" || open === "edit_list") && saveToGlobalLists && !state.isGlobalList) ? <Chip className="global_chip" label="Global list" sx={{ marginTop: "35px", marginLeft: "10px" }} /> : null}
                </Grid>
                {component === "drawer" ? null : <Grid item mt={3.5} xs={1} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}><CloseIcon className="edit_dialog_close" onClick={handleCloseDialog} /></Grid>}
            </Grid>
        )
    }

    const renderEditOptionsActions = () => {
        return open !== "global_lists" ? (
            <Grid container my={0.6} display={"flex"} alignItems={"center"}>
                {open === "edit_list" && !isGlobalList ? (
                    <Grid
                        item
                        xs={6}
                        ml={0.4}
                        display={"flex"}
                        justifyContent={"flex-start"}
                    >
                        <Button
                            startIcon={<DeleteIcon sx={{ fontSize: "18px" }} />}
                            className="add-delete-button"
                            onClick={() => {
                                handleDeleteDropdown(selectedDropdown);
                                handleCloseDialog();
                            }}
                        >
                            Delete List
                        </Button>
                    </Grid>
                ) : null}
                <Grid item xs={open === "edit_list" ? isGlobalList ? 12 : 5.75 : isGlobalList ? 12 : 11.85} display="flex" justifyContent="flex-end" alignItems="center" gap={1} >
                    <Button className="edit-dialog-cancel-btn" onClick={() => handleCloseDialog()} >
                        Cancel
                    </Button>
                    <Button
                        className={canSave === false ? "edit-dialog-save-btn-disable" : "edit-dialog-save-btn"}
                        variant="contained"
                        onClick={(event) => handleSave(event, names)}
                        disabled={canSave === false}
                    >
                        {open === "edit_list" ? !listId && saveToGlobalLists ? "Save" : "Update" : "Save"}
                    </Button>
                </Grid>
            </Grid >
        ) : null;
    }

    return (
        id === "introTextTE" || id === "globalList" || !newOpen ? <Dialog id={id} ref={dialogRef} open={open} classes={{ paper: "edit-dialog-paper" }} maxWidth="lg">
            <DialogTitle sx={{ margin: "0px", padding: "0px 24px 0px 24px" }}>
                {renderEditOptionsTitle("dialog")}
            </DialogTitle>
            <Divider className="edit-dialog-divider" />
            <DialogContent sx={{ padding: open === "global_lists" ? "0px" : "20px 24px" }}>
                {renderEditOptionsContent("dialog")}
            </DialogContent >
            <Divider className="edit-dialog-divider" />
            <DialogActions>
                {renderEditOptionsActions("dialog")}
            </DialogActions >
        </Dialog > : <Dialog open={open} anchor={"right"}
            TransitionComponent={SlideFromRight}

            PaperProps={{
                sx: {
                    position: 'fixed',
                    width: {
                        xs: '100%',
                        sm: '100%',
                        md: '60%',
                    },
                    maxWidth: '800px',
                    ml: {
                        xs: '0',
                        sm: '0',
                        md: '35%'
                    }, // 1200 / 2
                    height: '700px',
                    maxHeight: 'calc(100% - 64px)',
                    borderRadius: 2,
                    mr: '0'
                },

            }}
        >
            {renderEditOptionsTitle("drawer")}
            <Divider className="edit-dialog-divider" />
            <Box sx={{ height: "100%", overflow: "auto" }}>
                {renderEditOptionsContent("drawer")}
            </Box>
            <Divider className="edit-dialog-divider" />
            {renderEditOptionsActions("drawer")}
        </Dialog >
    );
}

export default EditOptionsDialog;